import { formatMoney } from '@lib/money'
import { formatInstant, unixToDate } from '@lib/datetime'
import { MemberBreakdownItem } from '@/utils/balanceCalculations'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import { AddPayUpPrefill } from './AddPayUpModal'

export const formatAmount = (cents: number, currencyCode: string): string =>
	formatMoney(Math.abs(cents), currencyCode)

export const formatDateKey = (unixSeconds: number): string =>
	formatInstant(unixToDate(unixSeconds), 'dateLong')

export const buildPrefillFromBreakdown = (
	targetUserId: number,
	item: MemberBreakdownItem,
): AddPayUpPrefill => {
	const fromUserId = item.amount > 0 ? item.otherUserId : targetUserId
	const toUserId = item.amount > 0 ? targetUserId : item.otherUserId
	return {
		fromUserId,
		toUserId,
		currencyCode: item.currencyCode,
		amount: Math.abs(item.amount),
	}
}

export type ExpenseInvolvement = { type: 'lent' | 'borrowed' | 'none'; amount: number }

export const getUserInvolvement = (
	expense: GetExpenseResponse,
	me: GetUserType | undefined,
): ExpenseInvolvement => {
	if (!me) return { type: 'none', amount: 0 }

	const isParticipant = expense.participants.some(p => p.userId === me.id)
	const isPayer = expense.paidByUserId === me.id

	if (!isParticipant && !isPayer) return { type: 'none', amount: 0 }

	const participantCount = expense.participants.length
	let userShare = 0

	if (isParticipant) {
		const participant = expense.participants.find(p => p.userId === me.id)
		if (expense.splitType === 'Equally') {
			userShare = expense.amount / participantCount
		} else if (participant?.splitAmount != null) {
			if (expense.splitType === 'Percent') {
				userShare = (expense.amount * participant.splitAmount) / 10000
			} else {
				userShare = participant.splitAmount
			}
		}
	}

	if (isPayer) {
		const lentAmount = expense.amount - userShare
		return { type: 'lent', amount: lentAmount }
	}

	return { type: 'borrowed', amount: userShare }
}

export const getInvolvementText = (
	expense: GetExpenseResponse,
	me: GetUserType | undefined,
): string => {
	const involvement = getUserInvolvement(expense, me)
	const formatted = formatAmount(Math.round(involvement.amount), expense.currencyCode)
	switch (involvement.type) {
		case 'lent':
			return `you lent ${formatted}`
		case 'borrowed':
			return `you borrowed ${formatted}`
		case 'none':
			return 'not involved'
	}
}

export type PayUpInvolvement = { type: 'paid' | 'received' | 'none'; text: string }

export const getPayUpInvolvement = (
	payUp: GetPayUpType,
	me: GetUserType | undefined,
): PayUpInvolvement => {
	if (!me) return { type: 'none', text: 'between others' }
	if (payUp.fromUserId === me.id) return { type: 'paid', text: 'you paid' }
	if (payUp.toUserId === me.id) return { type: 'received', text: 'you received' }
	return { type: 'none', text: 'between others' }
}

export type ActivityItem =
	| { kind: 'expense'; date: number; data: GetExpenseResponse }
	| { kind: 'payup'; date: number; data: GetPayUpType }
